import { useState, useEffect, useCallback } from "react";
import {
  startRegistration, startAuthentication, browserSupportsWebAuthn,
  platformAuthenticatorIsAvailable,
} from "@simplewebauthn/browser";
import { api } from "../lib/api";

// Passkeys.
//
// The two ceremonies are each two round trips: ask the server for options, hand them to
// the browser, send what the authenticator produced back to be verified. The challenge
// travels as an opaque signed token issued alongside the options and returned with the
// response — the server keeps no session for it.
//
// Errors from the WebAuthn API are not fit to show anyone: cancelling the system dialog
// throws NotAllowedError, which is indistinguishable from a genuine refusal and reads as
// a failure when it was a decision. So they are translated here rather than surfaced.
export const passkeysSupported = () => browserSupportsWebAuthn();

// Whether this machine has something that can actually verify a person — Touch ID,
// Windows Hello, an Android screen lock.
//
// Worth asking BEFORE offering the button. Registration demands
// userVerification: 'required', so a desktop with no biometric and no device PIN cannot
// satisfy it and the browser refuses with NotAllowedError — which is the same error as a
// cancelled prompt, and therefore says nothing about the real cause.
//
// A false answer is not "no passkeys": a security key or a phone over the QR flow still
// works. So this drives an explanation, never a disabled button.
export async function platformAuthenticatorAvailable() {
  try { return await platformAuthenticatorIsAvailable(); }
  catch (e) { return false; }
}

// WebAuthn only runs in a secure context, and Chromium additionally refuses the ceremony
// on a page whose TLS certificate is invalid — while still exposing the API, so nothing
// in the feature detection above catches it. There is no way to read "the certificate is
// bad" from script, so this reports what CAN be read and the UI names the possibility.
export const secureContext = () => typeof window !== "undefined" && window.isSecureContext === true;

// `where` matters. On the settings screen the user chose to add a passkey and a
// dismissed prompt is unambiguously a cancellation, so saying nothing is right. On the
// sign-in page it is not: NotAllowedError is ALSO what the browser throws when it finds
// no passkey for this site, and when the call arrives without user activation. Staying
// silent there produces a button that appears to do nothing at all — which is exactly
// how this was first reported.
function humanError(e, where = "settings") {
  const name = e?.name || "";
  // Always leave a trace: these names are the whole diagnosis and the UI deliberately
  // shows at most a sentence.
  if (name) console.warn(`[passkeys] ${where} ceremony failed: ${name}`, e);

  // Chromium refuses the ceremony on a page with an invalid TLS certificate and says so
  // in the message — but reports window.isSecureContext as TRUE, so no feature check can
  // see it beforehand. The message is the only signal there is, so it is read rather than
  // discarded into the generic NotAllowedError branch below.
  if (/certificate/i.test(e?.message || "")) {
    return "This site's TLS certificate is not valid, and browsers refuse to create or use a passkey on such a page. Fix the certificate, or use the address whose certificate is valid.";
  }

  if (name === "NotAllowedError" || name === "AbortError") {
    return where === "login"
      ? "No passkey was used. Either the prompt was dismissed, or this device has no passkey for this site yet."
      // Silence here was a mistake. Enrolment CAN fail for reasons that are not a
      // cancellation — a device with no fingerprint, face or PIN set up cannot satisfy
      // the required user verification, and a security key with no free slots cannot
      // store a discoverable credential. Saying nothing left the dialog sitting open
      // looking like it had worked, with an empty table behind it.
      : "Nothing was saved. Either you dismissed the prompt, or this device cannot create a passkey — it needs a fingerprint, face unlock or a device PIN set up first.";
  }
  if (name === "InvalidStateError") return "This device already has a passkey for your account.";
  if (name === "SecurityError") return "This site's address does not match what the passkey was created for.";
  return e?.message || "Something went wrong with that passkey.";
}

export function usePasskeys() {
  const [passkeys, setPasskeys] = useState([]);
  const [enabled, setEnabled] = useState(true);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    try {
      const d = await api.get("s1/api/passkeys");
      setPasskeys(d?.passkeys || []);
      setEnabled(d?.enabled !== false);
      setError("");
    } catch (e) {
      setError(e.message || "Could not load your passkeys.");
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const add = useCallback(async (name) => {
    setBusy(true); setError("");
    try {
      const { options, challenge } = await api.post("s1/api/passkeys/register/options", {});
      const response = await startRegistration({ optionsJSON: options });
      await api.post("s1/api/passkeys/register/verify", { response, challenge, name });
      await load();
      return true;
    } catch (e) {
      const message = humanError(e, "settings");
      if (message) setError(message);
      return false;
    } finally { setBusy(false); }
  }, [load]);

  const rename = useCallback(async (id, name) => {
    await api.patch(`s1/api/passkeys/${id}`, { name });
    await load();
  }, [load]);

  const remove = useCallback(async (id) => {
    await api.del(`s1/api/passkeys/${id}`);
    await load();
  }, [load]);

  return { passkeys, enabled, loading, busy, error, setError, add, rename, remove, reload: load };
}

// Sign-in. Separate from the hook above because it runs on the login page, where there is
// no session and nothing to list.
//
// Split in two ON PURPOSE. A browser only allows navigator.credentials.get() while it
// still holds "transient user activation" from the click, and awaiting a network request
// first can spend it — Safari is strict about this and throws NotAllowedError, which is
// indistinguishable from the user cancelling. So the challenge is fetched AHEAD of the
// click and the handler calls startAuthentication with nothing awaited before it.
export const fetchPasskeyChallenge = () => api.post("s1/api/passkeys/login/options", {});

// `prefetched` is what fetchPasskeyChallenge returned. Without one this falls back to
// fetching inline, which still works in Chrome and is better than not offering sign-in
// at all — but it is the slower, more fragile path.
export async function signInWithPasskey({ rememberme = false, prefetched } = {}) {
  const { options, challenge } = prefetched || (await fetchPasskeyChallenge());
  const response = await startAuthentication({ optionsJSON: options });
  return api.post("s1/api/passkeys/login/verify", { response, challenge, rememberme });
}

// A challenge is single-use and the server gives it five minutes, so a page left open
// needs a fresh one before the click rather than after the failure.
export const CHALLENGE_STALE_MS = 4 * 60 * 1000;

export { humanError as passkeyErrorMessage };
